import React, { useState, ReactElement, useEffect, useCallback } from "react";
import { gql, useQuery, useMutation } from "urql";
import { useLocation, useSearchParams } from "wouter";
import { NumericFormat } from "react-number-format";
import { Formik, Form, ErrorMessage, useField, useFormikContext, FieldHookConfig } from "formik";
import * as Yup from "yup";
import { Alert, AlertVariant } from "../../../components/src/Alert";

import { DateField, TextField, InstrumentInput, SubmitButton } from "../../../components/src/form";
import { serializeSwedenDate } from "../../../components/src/dateFormater";
import { FetchInstrumentAccruedInterestButton } from "../components/form/FetchInstrumentAccruedInterestButton";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";
import { AttachmentForm } from "./AttachmentForm";
import { SYSTEM_PARTY_ID } from "../Constants";

const GET_INSTRUMENTS = gql`
    query instruments($idIn: [GraphQLObjectId!]) {
        instruments(idIn: $idIn) {
            _id
            name
            bloombergTicker
            currency
        }
    }
`;

const CREATE_VALUATION = gql`
    mutation createValuation($input: CreateValuationInput!) {
        createValuation(input: $input) {
            _id
        }
    }
`;

const newKey = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

interface RecordInputProp {
    disabled: boolean;
    record: any;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    onDelete: Function;
}

const RecordInput = ({ record, onDelete, disabled }: RecordInputProp) => {
    const [type, setType] = useState(record.type);
    const [currency] = useState(record.currency);
    const [value, setValue] = useState(record.value);

    const handleTypeChange = (e) => {
        record.type = e.target.value; //!important: change the referenced field
        setType(e.target.value);
    };

    const handleValueChange = (e) => {
        record.value = e.floatValue; //!important: change the referenced field
        setValue(e.floatValue);
    };

    const remove = () => {
        onDelete(record);
    };

    // see graphql 'PriceType'
    const types = ["AccruedInterest", "CleanPrice", "Price", "PriceEstimate"];

    return (
        <div className="mb-4 flex flex-wrap -mx-2">
            <div className="w-full sm:w-4/12 md:w-4/12 px-2 mb-4">
                <select
                    name="type"
                    value={type}
                    onChange={handleTypeChange}
                    disabled={disabled}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                >
                    {types.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
            </div>

            <div className="w-full sm:w-4/12 md:w-4/12 px-2 mb-4">
                <NumericFormat
                    name="value"
                    value={value}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                    onValueChange={handleValueChange}
                    disabled={disabled}
                    thousandSeparator=" "
                    decimalSeparator="."
                />
            </div>
            <div className="w-full sm:w-2/12 md:w-2/12 px-2 mb-4">
                <input type="text" name="currency" value={currency} readOnly={true} className="border border-gray-300 rounded px-3 py-2 w-full" />
            </div>
            <div className="w-full sm:w-2/12 md:w-2/12 px-2 mb-4">
                <button type="button" onClick={remove} disabled={disabled} className="btn btn-danger btn-sm">
                    x
                </button>
            </div>
        </div>
    );
};

type RecordsFieldPropTypes = FieldHookConfig<any[]> & {
    label: string;
    className: string;
    disabled: boolean;
    currency: string;
};

const RecordsField = ({ currency, label, className, disabled, ...props }: RecordsFieldPropTypes) => {
    const [field] = useField(props);
    const { setFieldValue } = useFormikContext();

    const onAddNew = () => {
        field.value.push({ key: newKey(), type: "Price", currency: currency, value: 0.0 });
        setFieldValue(field.name, field.value);
    };

    const onDelete = (e) => {
        const item = field.value.find((v) => v.key === e.key);
        const ndx = field.value.indexOf(item);
        if (ndx > -1) {
            field.value.splice(ndx, 1);
            setFieldValue(field.name, field.value);
        }
    };

    const li: any[] = (field as unknown as any).value || []; // seems to be a race condition here

    return (
        <div className={"mb-4" + (className ? " " + className : "")}>
            <label>{label}</label> &nbsp;
            <button type="button" onClick={onAddNew} className="btn btn-secondary btn-sm">
                +
            </button>
            {li.map((record, index) => (
                <RecordInput key={`ri-${index}`} record={record} disabled={disabled} onDelete={onDelete} />
            ))}
        </div>
    );
};

export function AddValuationPage(): ReactElement {
    const [queryArgs] = useSearchParams();
    const [, navigate] = useLocation();
    const [idIn, setIdIn] = useState<string[]>(null);

    const [instrument, setInstrument] = useState(null);
    const [valuation, setValuation] = useState(null);
    const [instruments, setInstruments] = useState([]);
    const [attachments, setAttachments] = useState([]);

    const [{ fetching, data, error }] = useQuery({ query: GET_INSTRUMENTS, variables: { idIn }, requestPolicy: "network-only" });
    const [_, createValuation] = useMutation(CREATE_VALUATION);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({ color: "info", visible: false, message: "" });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useEffect(() => {
        setInstrument(null);
        setValuation(null);
        setInstruments(null);
        if (queryArgs.get("instrumentIdIn")) {
            setIdIn([queryArgs.get("instrumentIdIn")]);
        } else {
            setIdIn(null);
        }
    }, [queryArgs, setInstrument, setValuation, setInstruments]);

    useEffect(() => {
        if (data) {
            setInstruments(data.instruments);
            if (data.instruments.length === 1) {
                const instrumentIn = data.instruments[0];
                setInstrument(instrumentIn);
                setValuation({
                    instrumentId: instrumentIn._id,
                    date: new Date(),
                    bloombergTicker: instrumentIn.bloombergTicker,
                    source: "Bloomberg",
                    currency: instrumentIn.currency,
                    prices: [] // clear prices on instrument change})
                });
            }
        }
    }, [data]);

    const callBackOnChangeAttachment = useCallback((attachments) => {
        setAttachments(attachments);
    }, []);

    if (fetching)
        return (
            <div>
                <div>Loading</div>
            </div>
        );

    if (error)
        return (
            <div>
                <h3>Failed loading data</h3>
            </div>
        );

    return (
        <div className="page">
            <h2>Add valuation</h2>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 mb-4">
                    <label>Select instrument</label>
                    <InstrumentInput
                        name="Instrument1"
                        values={instruments ? instruments : []}
                        selected={instrument ? instrument._id : null}
                        className=""
                        onChange={(e) => {
                            const val = {
                                instrumentId: e._id,
                                date: new Date(),
                                bloombergTicker: e.bloombergTicker,
                                source: "Bloomberg",
                                currency: e.currency,
                                prices: [] // clear prices on instrument change
                            };
                            setInstrument(e);
                            setValuation(val);
                        }}
                    />
                </div>
                <div className="w-full md:w-6/12 px-2 mb-4"></div>
            </div>

            {instrument ? (
                <Formik
                    key={"ins_" + instrument._id}
                    enableReinitialize={true}
                    initialValues={valuation}
                    validationSchema={Yup.object({
                        date: Yup.date().max(new Date()).min(new Date("1900-01-01")),
                        source: Yup.string().required().min(2),
                        prices: Yup.array().min(1)
                    })}
                    onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                        // clean input
                        const input = {
                            instrumentId: submitValues.instrumentId,
                            date: serializeSwedenDate(submitValues.date),
                            source: submitValues.source,
                            bloombergTicker: submitValues.bloombergTicker,
                            prices: submitValues.prices.map((p) => {
                                delete p.key;
                                return p;
                            }),
                            attachments
                        };

                        createValuation({ input })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error, setErrors);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    // redirect to edit valuation page on success
                                    setSubmitting(false);
                                    navigate("/valuation/" + result.data.createValuation._id, { replace: true });
                                    return true;
                                }
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form autoComplete="off">
                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full md:w-6/12 px-2 mb-4">
                                    {instrument.bloombergTicker ? (
                                        <div>
                                            <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded text-sm">
                                                {instrument.bloombergTicker}
                                            </span>
                                            ,&nbsp;&nbsp;
                                            <span>({instrument.currency})</span>{" "}
                                        </div>
                                    ) : (
                                        <div>{instrument.currency}</div>
                                    )}

                                    <div className="flex flex-row items-center mt-4">
                                        <DateField className="" name="date" label="Date *" disabled={false} />
                                        <FetchInstrumentAccruedInterestButton />
                                    </div>

                                    <TextField name="source" label="Source *" type="text" className="" disabled={isSubmitting} />

                                    <RecordsField
                                        key={"ins_" + instrument._id}
                                        name="prices"
                                        label="Prices *"
                                        className=""
                                        currency={instrument.currency}
                                        disabled={isSubmitting}
                                    />
                                    <ErrorMessage name="prices" render={(msg) => <div className="text-red-600 italic pt-0.5">{msg}</div>} />
                                </div>

                                <div className="w-full md:w-6/12 px-2 mb-4"></div>
                            </div>

                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full px-2 mb-4">
                                    <AttachmentForm
                                        clientId={SYSTEM_PARTY_ID}
                                        attachments={attachments || []}
                                        onChange={callBackOnChangeAttachment}
                                    />
                                </div>
                            </div>

                            {alert.visible ? (
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full px-2 mb-4">
                                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                                            {alert.message}
                                        </Alert>
                                    </div>
                                </div>
                            ) : null}

                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full px-2 mb-4">
                                    <SubmitButton disabled={isSubmitting} label="Create" />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            ) : null}
        </div>
    );
}
